import { useMemo, type VoidFunctionComponent } from 'react';

import { REPORTS_V3 } from '../../../../../shared/featureFlags/flagNames';
import { BinaryFetcherName } from '../../../../../shared/fetchers';
import Locator from '../../../../../shared/Locator';
import { isFreeTier } from '../../../../../shared/subscriptions';
import { useParams } from '../../../../contexts/RouterContext';
import { window } from '../../../../globals';
import { useIsSecurityEnabled, useIsSuperFeatureEnabled } from '../../../../helpers/features';
import { useReleaseGroupAllProjects } from '../../../../hooks/api/releaseGroups';
import { Action, ResourceType, useHasPermission } from '../../../../shared/permissions';
import Subnav from '../../../generic/Subnav';
import { useCanDisplaySecurityOptions } from '../../generic/hooks';
import { ReportCategories, ReportCategoryTitles } from '../../generic/ReportGenerationV2/utils';
import type { ReportCategory } from '../../generic/shared/types';

/**
 * The Reports V3 routes use camelCase slugs (eg. "remediationGuidance") while the old
 * ReportCategories use kebab-case (eg. "remediation-guidance"). This map bridges
 * the mismatch for any category whose slug differs between the two systems.
 *
 * TODO: Once REPORTS_V3 is GA and the flag is removed, update ReportCategories
 * to use the camelCase values directly and remove this map.
 */
const reportsV3SlugOverrides: Partial<Record<ReportCategory, string>> = {
  [ReportCategories.RemediationGuidance]: 'remediationGuidance',
};

/**
 * Mini hook which just memoizes the enabled report types based on the
 * security and PDF reports feature flag and and the projects fetcher types.
 */
const useEnabledReportTypes = () => {
  const isSecurityAndPDFReportsEnabled = useCanDisplaySecurityOptions();
  const isSecurityEnabled = useIsSecurityEnabled();
  const params = useParams<{ projectGroupId: number }>();
  const allProjectLocators = useReleaseGroupAllProjects(params.projectGroupId).data ?? [];
  const areAllProjectsBinary = allProjectLocators.every(loc => {
    const { fetcher } = Locator.parse(loc);
    return fetcher === BinaryFetcherName;
  });

  return useMemo(() => {
    const excludedReports: string[] = [];
    if (!isSecurityAndPDFReportsEnabled) {
      excludedReports.push(ReportCategories.Vulnerability);
    }
    if (!isSecurityEnabled || areAllProjectsBinary) {
      excludedReports.push(ReportCategories.RemediationGuidance);
    }

    return Object.entries(ReportCategoryTitles).filter(([key]) => !excludedReports.includes(key));
  }, [isSecurityAndPDFReportsEnabled, isSecurityEnabled, areAllProjectsBinary]);
};

type Props = {
  baseUrl: string;
  releaseId?: number;
};

/**
 * ReleaseGroupSubnavItemReports is the Subnav item for the reports section of a release group.
 */
const ReleaseGroupSubnavItemReports: VoidFunctionComponent<Props> = ({
  baseUrl,
  releaseId,
}) => {
  const isNewReportsFlowEnabled = useIsSuperFeatureEnabled(REPORTS_V3);
  const shouldShowSettings = !isNewReportsFlowEnabled && !isFreeTier({
    plan: window?.user?.organization?.stripe_plan,
    stripePriceLimit: window?.user?.organization?.stripePriceLimit,
  }); // For settings

  const enabledReportTypes = useEnabledReportTypes();

  const hasPermission = useHasPermission();
  const canGenerateReport = hasPermission({ resourceType: ResourceType.ReleaseGroupReportAny, action: Action.Create })
    || (releaseId && hasPermission({
      resourceType: ResourceType.ReleaseGroupReport,
      action: Action.Create,
      releaseGroupId: releaseId,
    }));

  if (!canGenerateReport) {
    return null;
  }

  return (
    <Subnav.Dropdown
      to={`${baseUrl}/reports`}
      navKey="reports"
    >
      <Subnav.Dropdown.RootItem>
        Reports
      </Subnav.Dropdown.RootItem>
      {enabledReportTypes.map(([reportType, title]) => {
        const slug = isNewReportsFlowEnabled
          ? (reportsV3SlugOverrides[reportType as ReportCategory] ?? reportType)
          : reportType;

        return (
          <Subnav.Dropdown.Item
            key={slug}
            to={`${baseUrl}/reports/${slug}`}
            navKey={`reports/${slug}`}
          >
            {title}
          </Subnav.Dropdown.Item>
        );
      })}
      {shouldShowSettings && (
        <Subnav.Dropdown.Item
          to={`${baseUrl}/reports/settings`}
          navKey="reports/settings"
        >
          Settings
        </Subnav.Dropdown.Item>
      )}
    </Subnav.Dropdown>
  );
};

export default ReleaseGroupSubnavItemReports;
